import type { GestureResponderEvent } from "react-native";
import { useWindowDimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import {
  DownloadEnforcementBullets,
  DownloadEnforcementModalText,
} from "@/constants/DownloadEnforcement";
import { ModalTestID, TextTestID } from "@/constants/TestID";
import { TextSeparator } from "@/constants/Text";
import type { DownloadEnforcementModalProps } from "@/ui/components/web/DownloadEnforcementModal/types";
import SafeNativeModal from "@/ui/composed/modal/SafeNativeModal";
import {
  Button,
  FAIconName,
  Image,
  NavigationIconButton,
  Text,
} from "@/ui/core";
import { Box, HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { getEnvironmentQRCode } from "@/utils/qrCode.util";
import { joinTestIDs } from "@/utils/string.util";

// intrinsic dimensions of assets/images/download-enforcement-mobile.png, used to preserve aspect ratio
const HeroImageAspectRatio = 320 / 252;
// small horizontal breathing room so the mockups don't touch the screen edges
const HeroImageHorizontalPadding = 48;

// per Figma the desktop card hugs to 720 wide; height fits its content
const DesktopCardMaxWidth = 720;
// intrinsic dimensions of assets/images/download-enforcement-desktop.png
const DesktopHeroAspectRatio = 1005 / 947;
const DesktopHeroWidth = 360;
const DesktopHeroHeight = DesktopHeroWidth / DesktopHeroAspectRatio;
// how far the hero mockup bleeds past the card's bottom edge (clipped by overflow:hidden)
const DesktopHeroBleed = 32;
const QRCodeSize = 160;

const baseTestID = ModalTestID.DownloadEnforcement;

interface DownloadEnforcementCloseProps {
  onClose: () => void;
}

interface DownloadEnforcementVariantProps extends DownloadEnforcementCloseProps {
  onDownload: () => void;
}

const CloseButton = ({ onClose }: DownloadEnforcementCloseProps) => (
  <NavigationIconButton
    accessibilityLabel={DownloadEnforcementModalText.DismissAccessibilityLabel}
    onPress={onClose}
    testID={joinTestIDs(baseTestID, "close")}
    type="close"
  />
);

const Title = ({ isCentered = false }: { isCentered?: boolean }) => (
  <Text
    color={ColorToken.NeutralPrimary}
    testID={joinTestIDs(baseTestID, TextTestID.Title)}
    textAlign={isCentered ? "center" : "left"}
    variant="h3"
  >
    {DownloadEnforcementModalText.Title}
  </Text>
);

const Bullets = () => (
  <VStack gap={12} width="100%">
    {DownloadEnforcementBullets.map((bullet) => (
      <HStack gap={12} key={bullet.id}>
        <Text
          color={ColorToken.NeutralPrimary}
          testID={joinTestIDs(baseTestID, "bullet-dot", bullet.id)}
        >
          {TextSeparator.Dot}
        </Text>
        <Box flex={1} flexShrink={1}>
          <Text
            color={ColorToken.NeutralPrimary}
            testID={joinTestIDs(baseTestID, "bullet", bullet.id)}
          >
            {bullet.text}
          </Text>
        </Box>
      </HStack>
    ))}
  </VStack>
);

// Web-on-mobile variant: full-screen sheet with a download CTA and the
// phone-mockup hero bleeding off the bottom.
const MobileVariant = ({
  onClose,
  onDownload,
}: DownloadEnforcementVariantProps) => {
  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  // clamp to 0 so a very narrow viewport can't produce negative dimensions
  const clampedHeroWidth = Math.max(
    0,
    windowWidth - HeroImageHorizontalPadding,
  );
  const heroHeight = clampedHeroWidth / HeroImageAspectRatio;

  return (
    <VStack
      backgroundColor={ColorToken.Primary10}
      flex={1}
      paddingTop={insets.top}
    >
      <HStack justifyContent="flex-end" paddingHorizontal={16} paddingTop={12}>
        <CloseButton onClose={onClose} />
      </HStack>

      <VStack alignItems="center" gap={24} paddingHorizontal={32}>
        <Title isCentered />
        <Bullets />
        <Button
          leftIcon={FAIconName.Mobile}
          onPress={onDownload}
          testID={joinTestIDs(baseTestID, "download")}
          title={DownloadEnforcementModalText.ButtonTitle}
          variant="primary"
        />
      </VStack>

      {/* hero graphic fills the width and bleeds off the bottom of the screen, per Figma */}
      <Box
        alignItems="center"
        flex={1}
        justifyContent="flex-end"
        overflow="hidden"
      >
        <Image
          accessibilityLabel="Equip app preview"
          contentFit="contain"
          size={{ height: heroHeight, width: clampedHeroWidth }}
          testID={joinTestIDs(baseTestID, "hero")}
          uriSource={require("@/assets/images/download-enforcement-mobile.png")}
        />
      </Box>
    </VStack>
  );
};

const QRColumn = () => (
  <VStack alignItems="center" gap={16} maxWidth={QRCodeSize + 80}>
    <Image
      accessibilityLabel={DownloadEnforcementModalText.QRCodeAccessibilityLabel}
      contentFit="contain"
      size={{ height: QRCodeSize, width: QRCodeSize }}
      testID={joinTestIDs(baseTestID, "qr")}
      uriSource={getEnvironmentQRCode()}
    />
    <Text
      color={ColorToken.NeutralPrimary}
      testID={joinTestIDs(baseTestID, "qr-caption")}
      textAlign="center"
    >
      {DownloadEnforcementModalText.QRCaption}
    </Text>
  </VStack>
);

// Desktop web variant: centered card over a dimmed backdrop, with a QR code +
// caption on the left and the phone-mockup hero on the right.
const DesktopVariant = ({ onClose }: DownloadEnforcementCloseProps) => (
  <Box
    accessibilityLabel={DownloadEnforcementModalText.DismissAccessibilityLabel}
    alignItems="center"
    flex={1}
    justifyContent="center"
    onPress={onClose}
    style={{ backgroundColor: "rgba(0, 0, 0, 0.08)" }}
    testID={ModalTestID.ModalBackdrop}
  >
    <VStack
      backgroundColor={ColorToken.Primary10}
      borderRadius={24}
      gap={24}
      maxWidth={DesktopCardMaxWidth}
      // clicks inside the card must not bubble to the backdrop's dismiss handler
      onPress={(event: GestureResponderEvent) => event.stopPropagation()}
      // bottom padding is omitted so the hero mockup can bleed off the card's
      // bottom edge (clipped by overflow:hidden + borderRadius), per Figma
      overflow="hidden"
      paddingHorizontal={32}
      paddingTop={32}
      width="100%"
    >
      <HStack alignItems="flex-start" gap={16} justifyContent="space-between">
        <Box flex={1} flexShrink={1}>
          <Title />
        </Box>
        <CloseButton onClose={onClose} />
      </HStack>

      <Bullets />

      <HStack alignItems="flex-end" gap={16} justifyContent="space-between">
        <Box paddingBottom={32}>
          <QRColumn />
        </Box>
        {/* negative bottom margin pushes the mockup past the card edge so it's
            cropped by the card's overflow:hidden, matching Figma */}
        <Box marginBottom={-DesktopHeroBleed}>
          <Image
            accessibilityLabel="Equip app preview"
            contentFit="contain"
            size={{ height: DesktopHeroHeight, width: DesktopHeroWidth }}
            testID={joinTestIDs(baseTestID, "hero")}
            uriSource={require("@/assets/images/download-enforcement-desktop.png")}
          />
        </Box>
      </HStack>
    </VStack>
  </Box>
);

const DownloadEnforcementModal = ({
  isDesktop,
  isOpen,
  onClose,
  onDownload,
}: DownloadEnforcementModalProps) => (
  <SafeNativeModal
    animationType={isDesktop ? "fade" : "slide"}
    onRequestClose={onClose}
    testID={baseTestID}
    transparent={isDesktop}
    visible={isOpen}
  >
    {isDesktop ? (
      <DesktopVariant onClose={onClose} />
    ) : (
      <MobileVariant onClose={onClose} onDownload={onDownload} />
    )}
  </SafeNativeModal>
);

export default DownloadEnforcementModal;
